// Utilities
import { defineStore } from 'pinia'

import { Portal } from '@/types/portal'
import { BusinessProfile } from '@/types/businessProfile'
import { User } from '@/types/user'
import RoimaticConfiguration, {
  CustomLabelDetail
} from '@/types/roimaticConfigurations'
import { DIYDashboard } from '@/types/DIYDashboard'
import { Currency } from '@/types/currency'
import { BusinessProfileService } from '@/services'
import { Timezone } from '@/types/timezone'
import { SearchEngines } from '@/constants/catalogManager'
import { settingKeys } from '@/constants/settingKeys'

interface AppState {
  portal: Portal | null
  businessProfiles: BusinessProfile[]
  businessProfileUsers: User[]
  selectedBusinessProfileId: number | null
  sidebarDrawer: boolean
  rightPaneDrawer: boolean
  diyDashboards: DIYDashboard[]
  selectedBusinessProfileCurrency: Currency | null
  selectedBusinessProfileTimezone: Timezone | null
  roimaticConfiguration: RoimaticConfiguration | null
}

export const useAppStore = defineStore('app', {
  state: (): AppState => ({
    portal: null,
    businessProfiles: [],
    businessProfileUsers: [],
    selectedBusinessProfileId: null,
    sidebarDrawer: true,
    rightPaneDrawer: true,
    selectedBusinessProfileCurrency: null,
    selectedBusinessProfileTimezone: null,
    diyDashboards: [],
    roimaticConfiguration: null
  }),
  getters: {
    getPortalDomain(): string {
      if (import.meta.env.DEV) {
        return import.meta.env.VITE_PORTAL_DOMAIN || 'roimatic.adbox.pro'
      }
      return window.location.hostname
    },
    getSelectedBusinessProfile(): BusinessProfile | null {
      return (
        this.businessProfiles.find(
          businessProfile =>
            businessProfile.id === this.selectedBusinessProfileId
        ) || null
      )
    },
    getPortal(): Portal | null {
      return this.portal
    },
    getSelectedBusinessProfileId(): number {
      return this.selectedBusinessProfileId ?? 0
    },
    getSelectedBusinessProfileCurrency(): Currency | null {
      return this.selectedBusinessProfileCurrency
    },
    getRoimaticConfiguration(): RoimaticConfiguration | null {
      return this.roimaticConfiguration
    },
    getCusotmLabelDetails(): CustomLabelDetail[] {
      const catalogSettings =
        this.roimaticConfiguration?.catalogSettings?.destinationCatalogSettings
          ?.channelLevelSettings
      if (!catalogSettings) return []

      const allDetails: CustomLabelDetail[] = []
      const seenLabelTypeIds = new Set<number>()

      for (const engine of Object.keys(catalogSettings)) {
        const engineKey = Number(engine) as SearchEngines
        const details = catalogSettings[engineKey]?.customLabelDetails
        if (details) {
          for (const detail of details) {
            if (
              detail.labelTypeId !== undefined &&
              !seenLabelTypeIds.has(detail.labelTypeId)
            ) {
              seenLabelTypeIds.add(detail.labelTypeId)
              allDetails.push(detail)
            }
          }
        }
      }

      return allDetails
    },
    getSelectedBusinessProfileTimezone(): Timezone | null {
      return this.selectedBusinessProfileTimezone
    },
    getBusinessProfiles(): BusinessProfile[] {
      return this.businessProfiles
    }
  },
  actions: {
    setPortal(portal: Portal | null): void {
      this.portal = portal
    },
    setBusinessProfiles(businessProfiles: BusinessProfile[]): void {
      this.businessProfiles = businessProfiles
    },
    setSelectedBusinessProfileId(businessProfileId: number | null): void {
      this.selectedBusinessProfileId = businessProfileId
    },
    setSidebarDrawer(sidebarDrawer: boolean): void {
      this.sidebarDrawer = sidebarDrawer
    },
    setRightPaneDrawer(rightPaneDrawer: boolean): void {
      this.rightPaneDrawer = rightPaneDrawer
    },
    setSelectedBusinessProfileCurrency(currency: Currency | null) {
      this.selectedBusinessProfileCurrency = currency
    },
    setDIYDashboards(diyDashboards: DIYDashboard[]): void {
      this.diyDashboards = diyDashboards
    },
    setRoimaticConfiguraiton(roimaticConfig: RoimaticConfiguration): void {
      this.roimaticConfiguration = roimaticConfig
    },
    setBusinessProfile(
      id: number,
      updatedProfile: Partial<BusinessProfile>
    ): void {
      this.businessProfiles = this.businessProfiles.map(profile =>
        profile.id === id ? { ...profile, ...updatedProfile } : profile
      )
    },
    async fetchAndSetSkipAuditFlag(
      businessProfileService: BusinessProfileService = new BusinessProfileService()
    ): Promise<void> {
      const AUDIT_SKIP_SETTING_KEY = settingKeys.AUDIT_SKIP_SETTING_KEY
      const businessProfile = this.getSelectedBusinessProfile
      if (!businessProfile || businessProfile.isSkipAudit != null) return
      const settings = await businessProfileService.getBusinessProfileSettings(
        businessProfile.id,
        AUDIT_SKIP_SETTING_KEY
      )
      const auditSkipSettings = settings.find(
        s => s.key === AUDIT_SKIP_SETTING_KEY
      )
      const flag = auditSkipSettings
        ? auditSkipSettings.value === true || auditSkipSettings.value === 'true'
        : true

      this.setBusinessProfile(businessProfile.id, { isSkipAudit: flag })
    },
    async fetchAndSetIsPriceDropEnabledFlag(
      businessProfileService: BusinessProfileService = new BusinessProfileService()
    ): Promise<void> {
      const IS_PRICE_DROP_ENABLED_SETTING_KEY =
        settingKeys.IS_PRICE_DROP_ENABLED_SETTING_KEY
      const businessProfile = this.getSelectedBusinessProfile
      if (!businessProfile || businessProfile.isPriceDropEnabled != null) return
      const settings = await businessProfileService.getBusinessProfileSettings(
        businessProfile.id,
        IS_PRICE_DROP_ENABLED_SETTING_KEY
      )
      const isPriceDropSettings = settings.find(
        s => s.key === IS_PRICE_DROP_ENABLED_SETTING_KEY
      )
      const flag = isPriceDropSettings
        ? isPriceDropSettings.value === true ||
          isPriceDropSettings.value === 'true'
        : false

      this.setBusinessProfile(businessProfile.id, { isPriceDropEnabled: flag })
    },
    setSelectedBusinessProfileTimezone(timezone: Timezone | null) {
      this.selectedBusinessProfileTimezone = timezone
    }
  }
})
